import { DropdownActionItem, DropdownActions } from '../../../../../../widgets/dropdown';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC, useRef } from 'react';
import { T } from 'src/shared/helpers';
import CheckedIcon from 'src/shared/material_icons/action/check_circle.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete_outline.svg';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import LinkIcon from 'src/shared/material_icons/content/link.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import RetargetIcon from 'src/shared/material_icons/maps/my_location.svg';
import UncheckedIcon from 'src/shared/material_icons/toggle/radio_button_unchecked.svg';
import styled, { css } from 'styled-components';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { ComposerAttributeOptionNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerNodeValidationMessages } from '../helpers/composerAttributeValidation';
import { setComposerRowDragImage } from '../helpers/composerDragImage';
import ComposerValidationTooltip from './composerValidationTooltip';

type ComposerOptionRowAction = 'links' | 'duplicate' | 'retarget' | 'remove';

interface ComposerOptionTreeElementProps {
    option: ComposerAttributeOptionNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onMoveUp?: (option: ComposerAttributeOptionNode) => void;
    onMoveDown?: (option: ComposerAttributeOptionNode) => void;
    onDragStart?: (event: React.DragEvent<HTMLButtonElement>, option: ComposerAttributeOptionNode) => void;
    onDragEnd?: (event: React.DragEvent<HTMLButtonElement>, option: ComposerAttributeOptionNode) => void;
    onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
    onSetDefault?: (option: ComposerAttributeOptionNode) => void;
    onAction?: (action: ComposerOptionRowAction, option: ComposerAttributeOptionNode) => void;
    onVisibilityClick?: (option: ComposerAttributeOptionNode) => void;
    selected: boolean;
    hasLink?: boolean;
    shift?: boolean;
}

const Row = styled.div<{ $selected: boolean; $shift: boolean }>`
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 42px;
    align-items: center;
    gap: 7px;
    margin-left: ${(props) => (props.$shift ? '18px' : '0')};
    padding: 6px 6px 6px 8px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-sunken, #f4f7fa)')};
    border: 0;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);
    cursor: pointer;
    outline: none;
    user-select: none;

    ${(props) =>
        props.$shift &&
        css`
            width: calc(100% - 18px);
        `}

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    &::before {
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 0;
        display: ${(props) => (props.$selected ? 'block' : 'none')};
        width: 2.5px;
        background: var(--composer-accent, #e8650a);
        border-radius: 2px;
        content: '';
    }

    /* Row commands appear on hover, except the ones marked persistent: default, constraints and a disabled option must read at a glance. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    /* :focus-within alone would keep them lit after a plain click, since the row itself takes focus. */
    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }
`;

const Main = styled.div<{ $muted: boolean }>`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 8px;
    opacity: ${(props) => (props.$muted ? 0.5 : 1)};
`;

const Preview = styled.span`
    display: inline-flex;
    width: 26px;
    height: 26px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: var(--composer-kind-option, #b32d7d);

    img,
    svg {
        width: 100%;
        height: 100%;
        object-fit: contain;
        fill: currentColor;
    }
`;

const Name = styled.span`
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 1px;
`;

const Slot = styled.span`
    display: inline-flex;
    width: 28px;
    height: 28px;
`;

export const ComposerOptionTreeElement: FC<ComposerOptionTreeElementProps> = ({
    option,
    onSelect,
    onDoubleClick,
    selected,
    hasLink,
    shift = false,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver,
    onSetDefault,
    onAction,
    onVisibilityClick
}) => {
    const rowRef = useRef<HTMLDivElement>(null);

    return (
        <Row
            ref={rowRef}
            $selected={selected}
            $shift={shift}
            aria-selected={selected}
            onClick={(event) => onSelect(event, option)}
            onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, option) : undefined}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp' && event.altKey) {
                    event.preventDefault();
                    onMoveUp?.(option);
                }
                if (event.key === 'ArrowDown' && event.altKey) {
                    event.preventDefault();
                    onMoveDown?.(option);
                }
            }}
            role='option'
            tabIndex={0}
        >
            <Main $muted={option.enabled === false}>
                {option.brokenValues.size > 0 && <ComposerValidationTooltip messages={getComposerNodeValidationMessages(option)} />}
                <Preview>{option.img ? <img src={option.img} alt='' /> : <ImageIcon />}</Preview>
                <Name title={option.name}>{option.name}</Name>
            </Main>
            <Actions>
                {hasLink ? (
                    <ZTooltip title={T._('This option has constraints', 'Admin')}>
                        <ActionButton
                            $active
                            aria-label={T._('Open constraints', 'Admin')}
                            data-persistent='true'
                            onClick={(event) => {
                                event.stopPropagation();
                                onAction?.('links', option);
                            }}
                        >
                            <LinkIcon />
                        </ActionButton>
                    </ZTooltip>
                ) : (
                    <Slot />
                )}
                <ZTooltip title={option.isDefault ? T._('Default option', 'Admin') : T._('Set as default', 'Admin')}>
                    <ActionButton
                        $active={option.isDefault}
                        aria-label={option.isDefault ? T._('Default option', 'Admin') : T._('Set as default', 'Admin')}
                        data-persistent={option.isDefault ? 'true' : undefined}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSetDefault?.(option);
                        }}
                    >
                        {option.isDefault ? <CheckedIcon /> : <UncheckedIcon />}
                    </ActionButton>
                </ZTooltip>
                <ZTooltip title={option.enabled !== false ? T._('Disable this option', 'Admin') : T._('Enable this option', 'Admin')}>
                    <ActionButton
                        $off={option.enabled === false}
                        aria-label={option.enabled !== false ? T._('Disable this option', 'Admin') : T._('Enable this option', 'Admin')}
                        aria-pressed={option.enabled === false}
                        data-persistent={option.enabled === false ? 'true' : undefined}
                        onClick={(event) => {
                            event.stopPropagation();
                            onVisibilityClick?.(option);
                        }}
                    >
                        {option.enabled !== false ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </ActionButton>
                </ZTooltip>
                <DropdownActions
                    header={
                        <ActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                            <MoreIcon />
                        </ActionButton>
                    }
                    items={[
                        <DropdownActionItem
                            key='duplicate'
                            caption={T._('A copy with its actions', 'Admin')}
                            icon={<DuplicateIcon />}
                            onClick={() => onAction?.('duplicate', option)}
                        >
                            {T._('Duplicate', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem
                            key='retarget'
                            caption={T._('Point the selectors at another model', 'Admin')}
                            icon={<RetargetIcon />}
                            onClick={() => onAction?.('retarget', option)}
                        >
                            {T._('Retarget', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem
                            key='remove'
                            caption={T._('The option and its actions', 'Admin')}
                            danger
                            icon={<RemoveIcon />}
                            onClick={() => onAction?.('remove', option)}
                        >
                            {T._('Remove', 'Admin')}
                        </DropdownActionItem>
                    ]}
                    minWidth='260px'
                    orientation='RIGHT'
                />
                <ZTooltip title={T._('Drag to reorder · Alt+Arrow to move', 'Admin')}>
                    <ActionButton
                        aria-label={T._('Drag to reorder', 'Admin')}
                        draggable={!!onDragStart}
                        onDragEnd={(event) => onDragEnd?.(event, option)}
                        onDragStart={(event) => {
                            event.stopPropagation();
                            setComposerRowDragImage(event.dataTransfer, rowRef.current);
                            onDragStart?.(event, option);
                        }}
                    >
                        <DragIcon />
                    </ActionButton>
                </ZTooltip>
            </Actions>
        </Row>
    );
};
